import { midiToFreq, noiseBuffer, type AudioGraph } from "./engine.ts";

/**
 * Small synthesized instruments. Each call schedules a handful of nodes at
 * an absolute AudioContext time and lets them stop themselves.
 */
export interface VoiceOptions {
  readonly gain?: number;
  /** 0..1 amount sent to the shared reverb. */
  readonly space?: number;
  readonly duration?: number;
  /** -1 (left) .. 1 (right). */
  readonly pan?: number;
}

function output(graph: AudioGraph, bus: AudioNode, t: number, opts: VoiceOptions): GainNode {
  const { ctx } = graph;
  const amp = ctx.createGain();
  amp.gain.value = 1;
  let tail: AudioNode = amp;
  if (opts.pan && typeof ctx.createStereoPanner === "function") {
    const panner = ctx.createStereoPanner();
    panner.pan.value = opts.pan;
    amp.connect(panner);
    tail = panner;
  }
  tail.connect(bus);
  if (opts.space) {
    const send = ctx.createGain();
    send.gain.value = opts.space;
    tail.connect(send).connect(graph.reverbSend);
    // Disconnect the send after the voice has rung out.
    setTimeout(() => send.disconnect(), (t - ctx.currentTime + (opts.duration ?? 2) + 2) * 1000);
  }
  return amp;
}

function partial(graph: AudioGraph, out: AudioNode, freq: number, t: number, gain: number, decay: number, type: OscillatorType = "sine"): void {
  const { ctx } = graph;
  const osc = ctx.createOscillator();
  const env = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(gain, t + 0.006);
  env.gain.exponentialRampToValueAtTime(0.0001, t + decay);
  osc.connect(env).connect(out);
  osc.start(t);
  osc.stop(t + decay + 0.05);
}

/** Celesta / glockenspiel: slightly inharmonic sine partials, bright attack. */
export function bell(graph: AudioGraph, bus: AudioNode, midi: number, t: number, opts: VoiceOptions = {}): void {
  const freq = midiToFreq(midi);
  const decay = opts.duration ?? 1.6;
  const out = output(graph, bus, t, { space: 0.35, ...opts, duration: decay });
  const g = opts.gain ?? 0.16;
  partial(graph, out, freq, t, g, decay);
  partial(graph, out, freq * 2.0, t, g * 0.32, decay * 0.55);
  partial(graph, out, freq * 2.76, t, g * 0.18, decay * 0.35);
  partial(graph, out, freq * 5.4, t, g * 0.07, decay * 0.18);
}

/** Wooden mallet: round fundamental, a quick 4th-harmonic knock. */
export function marimba(graph: AudioGraph, bus: AudioNode, midi: number, t: number, opts: VoiceOptions = {}): void {
  const freq = midiToFreq(midi);
  const decay = opts.duration ?? 0.55;
  const out = output(graph, bus, t, { space: 0.18, ...opts, duration: decay });
  const g = opts.gain ?? 0.2;
  partial(graph, out, freq, t, g, decay);
  partial(graph, out, freq * 3.99, t, g * 0.22, decay * 0.18);
  partial(graph, out, freq * 9.8, t, g * 0.05, 0.03);
}

/** Soft plucked string / kalimba: triangle through a closing low-pass. */
export function pluck(graph: AudioGraph, bus: AudioNode, midi: number, t: number, opts: VoiceOptions = {}): void {
  const { ctx } = graph;
  const freq = midiToFreq(midi);
  const decay = opts.duration ?? 0.9;
  const out = output(graph, bus, t, { space: 0.22, ...opts, duration: decay });
  const osc = ctx.createOscillator();
  const filter = ctx.createBiquadFilter();
  const env = ctx.createGain();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(freq, t);
  filter.type = "lowpass";
  filter.Q.value = 0.8;
  filter.frequency.setValueAtTime(Math.min(9000, freq * 8), t);
  filter.frequency.exponentialRampToValueAtTime(Math.max(300, freq * 1.5), t + decay * 0.6);
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(opts.gain ?? 0.16, t + 0.008);
  env.gain.exponentialRampToValueAtTime(0.0001, t + decay);
  osc.connect(filter).connect(env).connect(out);
  osc.start(t);
  osc.stop(t + decay + 0.05);
}

/** Warm sustained chord tone (two detuned oscillators, slow swell). */
export function pad(graph: AudioGraph, bus: AudioNode, midi: number, t: number, opts: VoiceOptions = {}): void {
  const { ctx } = graph;
  const freq = midiToFreq(midi);
  const length = opts.duration ?? 2.5;
  const out = output(graph, bus, t, { space: 0.45, ...opts, duration: length });
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 1400;
  filter.Q.value = 0.3;
  const env = ctx.createGain();
  const g = opts.gain ?? 0.05;
  env.gain.setValueAtTime(0.0001, t);
  env.gain.linearRampToValueAtTime(g, t + Math.min(0.7, length * 0.35));
  env.gain.setValueAtTime(g, t + length * 0.7);
  env.gain.linearRampToValueAtTime(0.0001, t + length);
  filter.connect(env).connect(out);
  for (const [type, detune] of [
    ["triangle", -6],
    ["sine", 5],
  ] as const) {
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    osc.detune.value = detune;
    osc.connect(filter);
    osc.start(t);
    osc.stop(t + length + 0.05);
  }
}

/** Electric-piano-like chord tone with a gentle tremolo. */
export function keys(graph: AudioGraph, bus: AudioNode, midi: number, t: number, opts: VoiceOptions = {}): void {
  const { ctx } = graph;
  const freq = midiToFreq(midi);
  const length = opts.duration ?? 2;
  const out = output(graph, bus, t, { space: 0.3, ...opts, duration: length });
  const env = ctx.createGain();
  const g = opts.gain ?? 0.07;
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(g, t + 0.012);
  env.gain.exponentialRampToValueAtTime(g * 0.35, t + 0.5);
  env.gain.exponentialRampToValueAtTime(0.0001, t + length);
  const tremolo = ctx.createGain();
  tremolo.gain.value = 0.85;
  const lfo = ctx.createOscillator();
  const lfoDepth = ctx.createGain();
  lfo.frequency.value = 4.2;
  lfoDepth.gain.value = 0.15;
  lfo.connect(lfoDepth).connect(tremolo.gain);
  env.connect(tremolo).connect(out);
  const osc = ctx.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(freq, t);
  const bark = ctx.createOscillator();
  const barkGain = ctx.createGain();
  bark.frequency.setValueAtTime(freq * 2, t);
  barkGain.gain.setValueAtTime(0.25, t);
  barkGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
  osc.connect(env);
  bark.connect(barkGain).connect(env);
  for (const node of [osc, bark, lfo]) {
    node.start(t);
    node.stop(t + length + 0.05);
  }
}

/** Round sub bass note. */
export function bass(graph: AudioGraph, bus: AudioNode, midi: number, t: number, opts: VoiceOptions = {}): void {
  const { ctx } = graph;
  const freq = midiToFreq(midi);
  const length = opts.duration ?? 1;
  const out = output(graph, bus, t, { ...opts, space: 0, duration: length });
  const env = ctx.createGain();
  const g = opts.gain ?? 0.14;
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(g, t + 0.02);
  env.gain.exponentialRampToValueAtTime(g * 0.5, t + length * 0.5);
  env.gain.exponentialRampToValueAtTime(0.0001, t + length);
  const osc = ctx.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(freq, t);
  const body = ctx.createOscillator();
  const bodyGain = ctx.createGain();
  body.type = "triangle";
  body.frequency.setValueAtTime(freq * 2, t);
  bodyGain.gain.value = 0.18;
  osc.connect(env);
  body.connect(bodyGain).connect(env);
  env.connect(out);
  for (const node of [osc, body]) {
    node.start(t);
    node.stop(t + length + 0.05);
  }
}

/** Filtered noise: shaker ticks, brushes and whooshes. */
export function noise(
  graph: AudioGraph,
  bus: AudioNode,
  t: number,
  opts: VoiceOptions & { from?: number; to?: number; q?: number; type?: BiquadFilterType } = {},
): void {
  const { ctx } = graph;
  const length = opts.duration ?? 0.06;
  const out = output(graph, bus, t, { ...opts, duration: length });
  const source = ctx.createBufferSource();
  source.buffer = noiseBuffer(ctx);
  const filter = ctx.createBiquadFilter();
  filter.type = opts.type ?? "bandpass";
  filter.Q.value = opts.q ?? 1.2;
  filter.frequency.setValueAtTime(opts.from ?? 6000, t);
  if (opts.to) filter.frequency.exponentialRampToValueAtTime(opts.to, t + length);
  const env = ctx.createGain();
  const g = opts.gain ?? 0.05;
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(g, t + Math.min(0.02, length * 0.3));
  env.gain.exponentialRampToValueAtTime(0.0001, t + length);
  source.connect(filter).connect(env).connect(out);
  source.start(t, Math.random() * 0.5);
  source.stop(t + length + 0.05);
}

/** A soft wooden knock with a dropping pitch: "not there", never harsh. */
export function thud(graph: AudioGraph, bus: AudioNode, t: number, opts: VoiceOptions = {}): void {
  const { ctx } = graph;
  const out = output(graph, bus, t, { ...opts, space: 0.08, duration: 0.3 });
  const osc = ctx.createOscillator();
  const env = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(190, t);
  osc.frequency.exponentialRampToValueAtTime(85, t + 0.18);
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(opts.gain ?? 0.22, t + 0.008);
  env.gain.exponentialRampToValueAtTime(0.0001, t + 0.24);
  osc.connect(env).connect(out);
  osc.start(t);
  osc.stop(t + 0.3);
}
